import { Link } from 'react-router';
import { AlertTriangle, Clock } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import type { CorrectiveAction } from '@/types';
import {
  TRIGGER_BADGES,
  TRIGGER_LABELS,
  CA_STATUS_BADGES,
  CA_STATUS_LABELS,
  isOverdue,
} from '../_labels';

export function CorrectiveActionsSection({ actions }: { actions?: CorrectiveAction[] }) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Acciones Correctivas</h2>
      </div>
      {!actions?.length ? (
        <EmptyState
          title="Sin acciones correctivas"
          description="No tienes acciones correctivas asignadas."
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {actions.map((action) => {
            const overdue = isOverdue(action.due_date, action.status);
            return (
              <Link
                key={action.id}
                to={`/competency/corrective-actions/${action.id}`}
                className={cn(
                  'block rounded-xl border bg-card p-5 transition-colors hover:bg-accent/50',
                  overdue ? 'border-red-300 bg-red-50/50' : 'border-border',
                )}
              >
                <p className="text-sm text-card-foreground">{action.description}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      TRIGGER_BADGES[action.trigger_type],
                    )}
                  >
                    {TRIGGER_LABELS[action.trigger_type] ?? action.trigger_type}
                  </span>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-xs font-medium',
                      CA_STATUS_BADGES[action.status],
                    )}
                  >
                    {CA_STATUS_LABELS[action.status]}
                  </span>
                  {overdue && (
                    <span className="flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                      <Clock className="h-3 w-3" /> Vencida
                    </span>
                  )}
                </div>
                <div className="mt-3 space-y-0.5 text-xs text-muted-foreground">
                  {action.created_at && <p>Creada: {formatDate(action.created_at)}</p>}
                  {action.due_date && (
                    <p className={cn(overdue && 'font-semibold text-red-600')}>
                      Vence: {formatDate(action.due_date)}
                    </p>
                  )}
                  {action.resolved_at && <p>Resuelta: {formatDate(action.resolved_at)}</p>}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
